import { RefreshCw, ChevronDown, ChevronRight, Info, X } from "lucide-react";
import { Fragment, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  devMetricsApi,
  type PerfRunResult,
  type PerfRunDetail,
  type PerfRunStatus,
  type PerfScenarioSummary,
} from "../api/devMetrics";
import { PerfRegressionBadge } from "./PerfRegressionBadge";
import { formatDateTime } from "@/utils/formatDate";

const STATUS_COLOR: Record<PerfRunStatus, string> = {
  passed: "text-emerald-400",
  failed: "text-red-400",
  error: "text-amber-400",
  running: "text-blue-400",
};
const STATUS_LABEL: Record<PerfRunStatus, string> = {
  passed: "OK",
  failed: "FAILED",
  error: "ERROR",
  running: "RUNNING",
};
const DOT_COLOR: Record<PerfRunStatus, string> = {
  passed: "bg-emerald-500",
  failed: "bg-red-500",
  error: "bg-amber-500",
  running: "bg-blue-500 animate-pulse",
};

function fmtTimestamp(iso: string): string {
  const d = new Date(iso);
  return (
    `${String(d.getDate()).padStart(2, "0")}.${String(d.getMonth() + 1).padStart(2, "0")} ` +
    `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`
  );
}

function fmtMs(v: number | null | undefined): string {
  if (v == null) return "—";
  if (v >= 1000) return `${(v / 1000).toFixed(2)}s`;
  if (v >= 100) return `${Math.round(v)}`;
  return v.toFixed(1);
}

function fmtMsDiff(v: number | null | undefined): string {
  if (v == null) return "—";
  const sign = v > 0 ? "+" : "";
  if (Math.abs(v) >= 1000) return `${sign}${(v / 1000).toFixed(2)}s`;
  if (Math.abs(v) >= 100) return `${sign}${Math.round(v)}`;
  return `${sign}${v.toFixed(1)}`;
}

function fmtRatio(v: number | null | undefined): string {
  if (v == null) return "—";
  return `${Math.round(v * 100)}%`;
}

function fmtBytes(v: number | null | undefined): string {
  if (v == null) return "—";
  if (v < 1024) return `${Math.round(v)} B`;
  if (v < 1024 * 1024) return `${(v / 1024).toFixed(1)} KB`;
  return `${(v / 1024 / 1024).toFixed(2)} MB`;
}

function InfoBadge({ text }: { text: string }) {
  return (
    <span
      className="ml-1 inline-flex text-gray-500 hover:text-gray-300 cursor-help align-middle"
      title={text}
    >
      <Info size={11} />
    </span>
  );
}

// ── Grupowanie scenariuszy ──────────────────────────────────────────────────
// scenarios_summary keys: "<scenario>__<target>". Grupujemy po scenariuszu,
// w każdej grupie 2 wiersze (internal + public) + 1 diff (public - internal).

interface GroupedScenario {
  scenario: string;
  internal: PerfScenarioSummary | null;
  public: PerfScenarioSummary | null;
}

function groupByScenario(
  summary: Record<string, PerfScenarioSummary>,
): GroupedScenario[] {
  const map = new Map<string, GroupedScenario>();
  for (const [key, val] of Object.entries(summary)) {
    const lastUnderscore = key.lastIndexOf("__");
    if (lastUnderscore === -1) continue;
    const scenario = key.slice(0, lastUnderscore);
    const target = key.slice(lastUnderscore + 2);
    if (!map.has(scenario)) {
      map.set(scenario, { scenario, internal: null, public: null });
    }
    const g = map.get(scenario)!;
    if (target === "internal") g.internal = val;
    else if (target === "public") g.public = val;
  }
  return Array.from(map.values()).sort((a, b) =>
    a.scenario.localeCompare(b.scenario),
  );
}

function ScenarioRow({
  data,
  target,
  flagged,
}: {
  data: PerfScenarioSummary | null;
  target: "internal" | "public";
  flagged: boolean;
}) {
  if (!data) {
    return (
      <tr className="text-gray-600 italic">
        <td className="px-3 py-1.5">↳ {target}</td>
        <td colSpan={9} className="text-gray-600">brak danych</td>
      </tr>
    );
  }
  if (data.error) {
    return (
      <tr className="bg-red-900/10">
        <td className="px-3 py-1.5">↳ {target}</td>
        <td colSpan={9} className="text-red-400">ERROR: {data.error}</td>
      </tr>
    );
  }
  const lowBytes =
    target === "internal" &&
    data.bytes_per_tile_avg != null &&
    data.bytes_per_tile_avg < 8 * 1024;
  const targetBadge =
    target === "internal" ? (
      <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-teal-400">
        ↳ internal
      </span>
    ) : (
      <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-violet-400">
        ↳ public
      </span>
    );
  return (
    <tr className={flagged ? "bg-amber-900/15" : ""}>
      <td className="px-3 py-1.5 whitespace-nowrap pl-6">
        {targetBadge}
        {lowBytes && (
          <span
            className="ml-2 text-amber-400 text-[10px]"
            title="bytes_per_tile <8 KB: prawdopodobnie puste PNG (axis order WMS 1.3.0)"
          >
            empty?
          </span>
        )}
      </td>
      <td className="text-right px-2 py-1.5 text-gray-500 tabular-nums">{data.vus ?? "—"}</td>
      <td className="text-right px-2 py-1.5 text-gray-500 tabular-nums">{data.iterations ?? "—"}</td>
      <td className="text-right px-2 py-1.5 text-gray-400 tabular-nums">{fmtMs(data.p50_ms)}</td>
      <td className="text-right px-2 py-1.5 text-gray-300 tabular-nums">{fmtMs(data.p90_ms)}</td>
      <td className={`text-right px-2 py-1.5 tabular-nums font-semibold ${flagged ? "text-amber-300" : "text-gray-100"}`}>
        {fmtMs(data.p95_ms)}
      </td>
      <td className="text-right px-2 py-1.5 text-gray-400 tabular-nums">{fmtMs(data.p99_ms)}</td>
      <td className="text-right px-2 py-1.5 text-gray-500 tabular-nums">
        {data.throughput_rps != null ? data.throughput_rps.toFixed(1) : "—"}
      </td>
      <td className="text-right px-2 py-1.5 tabular-nums">
        <span className={(data.error_rate ?? 0) > 0.01 ? "text-red-400" : "text-gray-500"}>
          {fmtRatio(data.error_rate)}
        </span>
      </td>
      <td className="text-right px-2 py-1.5 text-gray-400 tabular-nums">{fmtRatio(data.cache_hit_ratio)}</td>
      <td className="text-right px-3 py-1.5 text-gray-500 tabular-nums">{fmtBytes(data.bytes_per_tile_avg)}</td>
    </tr>
  );
}

function DiffRow({ group }: { group: GroupedScenario }) {
  const i = group.internal;
  const p = group.public;
  if (!i || !p || i.error || p.error || i.p95_ms == null || p.p95_ms == null) {
    return null;
  }
  const diffP95 = p.p95_ms - i.p95_ms;
  const diffPct = i.p95_ms > 0 ? (diffP95 / i.p95_ms) * 100 : 0;
  const isBad = diffP95 > 50 || diffPct > 25;
  return (
    <tr className="text-[11px] border-t border-dashed border-gray-800/60">
      <td className="px-3 py-1 pl-6 text-gray-500 italic">
        Δ (public − internal)
        <InfoBadge text="Różnica p95 między ścieżką użytkownika (przez ALB+Django) a wewnętrzną (bezpośrednio nginx→QGIS). To koszt warstwy backendu." />
      </td>
      <td colSpan={4} className="px-2 py-1"></td>
      <td className={`text-right px-2 py-1 tabular-nums font-semibold ${isBad ? "text-amber-300" : "text-gray-400"}`}>
        {fmtMsDiff(diffP95)} ms
        <span className={`ml-1 text-[10px] ${isBad ? "text-amber-400" : "text-gray-600"}`}>
          ({diffPct >= 0 ? "+" : ""}{Math.round(diffPct)}%)
        </span>
      </td>
      <td colSpan={5} className="px-2 py-1"></td>
    </tr>
  );
}

function SampleThumbnails({
  samples,
  scenariosSummary,
}: {
  samples: Record<string, string>;
  scenariosSummary: Record<string, PerfScenarioSummary>;
}) {
  const keys = Object.keys(scenariosSummary).sort();
  if (!keys.length) return null;
  return (
    <details className="px-4 py-2 border-t border-gray-800 text-xs" open>
      <summary className="text-gray-400 cursor-pointer select-none mb-2">
        Sample tile per scenariusz × target
        <InfoBadge text="Jeden tile (centrum projektu, default zoom) zapisany po każdym wykonaniu k6. Pozwala zobaczyć wzrokowo czy mapa nie jest pusta / niekompletna." />
      </summary>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-1">
        {keys.map((k) => {
          const url = samples[k];
          const summary = scenariosSummary[k];
          const bytes = summary?.bytes_per_tile_avg;
          const suspicious = bytes != null && bytes < 8 * 1024;
          return (
            <div
              key={k}
              className={`flex flex-col gap-1 ${suspicious ? "ring-1 ring-amber-500/60 rounded" : ""}`}
            >
              <div className="text-[10px] text-gray-500 font-mono truncate" title={k}>
                {k}
              </div>
              {url ? (
                <a href={url} target="_blank" rel="noreferrer" className="block">
                  <img
                    src={url}
                    alt={k}
                    className="w-full h-auto rounded border border-gray-800 bg-white/5"
                    loading="lazy"
                  />
                </a>
              ) : (
                <div className="aspect-square flex items-center justify-center text-[10px] text-gray-600 border border-dashed border-gray-700 rounded">
                  brak sample
                </div>
              )}
              <div className="text-[10px] text-gray-600 font-mono">
                {fmtBytes(bytes)}
                {suspicious && (
                  <span className="text-amber-400 ml-1">⚠ puste?</span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </details>
  );
}

function ScenarioTable({
  scenarios,
  flags,
}: {
  scenarios: Record<string, PerfScenarioSummary>;
  flags: string[];
}) {
  const grouped = groupByScenario(scenarios);
  if (grouped.length === 0) {
    return <div className="text-xs text-gray-500 px-4 py-3">Brak danych.</div>;
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs">
        <thead>
          <tr className="text-gray-500 border-b border-gray-800 text-left">
            <th className="px-3 py-1.5 font-medium">scenariusz / target</th>
            <th className="text-right px-2 py-1.5 font-medium">
              VUs
              <InfoBadge text="Virtual users — ile równoległych klientów symulujemy." />
            </th>
            <th className="text-right px-2 py-1.5 font-medium">
              iter
              <InfoBadge text="Łączna liczba zapytań WMS GetMap w tym scenariuszu." />
            </th>
            <th className="text-right px-2 py-1.5 font-medium">p50</th>
            <th className="text-right px-2 py-1.5 font-medium">p90</th>
            <th className="text-right px-2 py-1.5 font-medium">
              p95
              <InfoBadge text="95% requestów było szybsze niż ta wartość (ms). Zwykle używane do oceny ogona długich odpowiedzi." />
            </th>
            <th className="text-right px-2 py-1.5 font-medium">p99</th>
            <th className="text-right px-2 py-1.5 font-medium">
              RPS
              <InfoBadge text="Requests per second — przepustowość." />
            </th>
            <th className="text-right px-2 py-1.5 font-medium">err</th>
            <th className="text-right px-2 py-1.5 font-medium">
              HIT%
              <InfoBadge text="% odpowiedzi z headera X-Cache-Status=HIT (tile serwowane z nginx cache zamiast renderowane przez QGIS Server)." />
            </th>
            <th className="text-right px-3 py-1.5 font-medium">
              B/tile
              <InfoBadge text="Średni rozmiar tile'a. <8 KB = prawdopodobnie puste PNG (axis order EPSG źle)." />
            </th>
          </tr>
        </thead>
        <tbody className="font-mono">
          {grouped.map((g, idx) => {
            const internalFlagged = flags.includes(`${g.scenario}__internal`);
            const publicFlagged = flags.includes(`${g.scenario}__public`);
            return (
              <Fragment key={g.scenario}>
                <tr className={idx > 0 ? "border-t-2 border-gray-800 bg-gray-800/30" : "bg-gray-800/30"}>
                  <td colSpan={11} className="px-3 py-1.5 text-gray-200 font-semibold text-[11px] uppercase tracking-wide">
                    {g.scenario}
                  </td>
                </tr>
                <ScenarioRow data={g.internal} target="internal" flagged={internalFlagged} />
                <ScenarioRow data={g.public} target="public" flagged={publicFlagged} />
                <DiffRow group={g} />
              </Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function PerfRunDetailPanel({ runId }: { runId: string }) {
  const { data: detail, isLoading } = useQuery<PerfRunDetail>({
    queryKey: ["dev-metrics-perf-run-detail", runId],
    queryFn: () => devMetricsApi.getPerfRunDetail(runId),
  });

  if (isLoading) {
    return <div className="px-4 py-3 text-xs text-gray-500">Ładowanie...</div>;
  }
  if (!detail) {
    return <div className="px-4 py-3 text-xs text-gray-500">Brak danych.</div>;
  }

  return (
    <div className="bg-gray-950/50 border-t border-gray-800">
      <ScenarioTable scenarios={detail.scenarios_summary} flags={detail.regression_flags} />

      <SampleThumbnails samples={detail.samples || {}} scenariosSummary={detail.scenarios_summary} />

      {detail.failure_summary && (
        <div className="px-4 py-2 border-t border-gray-800 text-xs">
          <span className="text-gray-500 font-medium">Uwagi:</span>
          <pre className="text-amber-300 whitespace-pre-wrap mt-1">{detail.failure_summary}</pre>
        </div>
      )}

      {detail.cw_snapshot && Object.keys(detail.cw_snapshot).length > 0 && (
        <details className="px-4 py-2 border-t border-gray-800 text-xs">
          <summary className="text-gray-500 cursor-pointer">CloudWatch snapshot okna runu</summary>
          <pre className="text-gray-400 whitespace-pre-wrap mt-2 font-mono">
            {JSON.stringify(detail.cw_snapshot, null, 2)}
          </pre>
        </details>
      )}

      {detail.raw_s3_prefix && (
        <div className="px-4 py-2 border-t border-gray-800 text-[11px] text-gray-500">
          Raw k6 JSON: <code className="text-gray-400">s3://&lt;media&gt;/{detail.raw_s3_prefix}/</code>
        </div>
      )}
    </div>
  );
}

// ── Timeline chart ─────────────────────────────────────────────────────────

import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";

const SERIES_COLORS = [
  "#3b82f6", // blue
  "#a855f7", // violet
  "#10b981", // emerald
  "#f59e0b", // amber
  "#ec4899", // pink
  "#06b6d4", // cyan
  "#ef4444", // red
  "#84cc16", // lime
];

function PerfTimelineChart({ runs }: { runs: PerfRunResult[] }) {
  const successful = runs
    .filter((r) => r.status === "passed" || r.status === "failed")
    .slice(0, 10)
    .reverse();
  if (successful.length === 0) return null;

  const allKeys = new Set<string>();
  for (const r of successful) {
    for (const k of Object.keys(r.scenarios_summary || {})) {
      const s = r.scenarios_summary[k];
      if (s && s.p95_ms != null && !s.error) allKeys.add(k);
    }
  }
  const keys = Array.from(allKeys).sort();

  const chartData = successful.map((r) => {
    const point: Record<string, string | number | null> = {
      ts: r.started_at,
      label: fmtTimestamp(r.started_at),
    };
    for (const k of keys) {
      const s = r.scenarios_summary?.[k];
      point[k] = s && s.p95_ms != null && !s.error ? s.p95_ms : null;
    }
    return point;
  });

  // Latency spans 2-3 orders of magnitude (cache HIT ~1ms vs cold render
  // ~500ms) — linear y-axis collapses small values onto x=0. Log scale keeps
  // all series readable. Log can't render 0/negative — clamp to 0.1 ms.
  function clampLog(v: number | null): number | null {
    if (v == null) return null;
    return v < 0.1 ? 0.1 : v;
  }
  const logData = chartData.map((p) => {
    const out: Record<string, string | number | null> = { label: p.label, ts: p.ts };
    for (const k of keys) out[k] = clampLog(p[k] as number | null);
    return out;
  });

  function fmtMsTick(v: number): string {
    if (v >= 1000) return `${(v / 1000).toFixed(0)}s`;
    if (v >= 1) return `${Math.round(v)}`;
    return v.toFixed(1);
  }

  return (
    <div className="bg-gray-950/50 border-t border-gray-800 px-3 py-3">
      <div className="text-[11px] text-gray-400 mb-1 px-1">
        p95 (ms, skala logarytmiczna) — ostatnie {successful.length} udanych runów
        <InfoBadge text="Skala log bo p95 dla cache HIT (~1 ms) i cold render (~500 ms) różni się o 2-3 rzędy wielkości — liniowo małe wartości zlewają się z zerem. Linia niżej = szybciej. Porównaj punkt przed i po wdrożeniu zmiany." />
      </div>
      <ResponsiveContainer width="100%" height={220}>
        <LineChart data={logData} margin={{ top: 4, right: 12, bottom: 0, left: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#374151" strokeOpacity={0.3} />
          <XAxis
            dataKey="label"
            tick={{ fontSize: 10, fill: "#9ca3af" }}
            axisLine={false}
            tickLine={false}
          />
          <YAxis
            scale="log"
            domain={[0.1, "auto"]}
            allowDataOverflow
            ticks={[0.1, 1, 10, 100, 1000, 10000]}
            tick={{ fontSize: 10, fill: "#9ca3af" }}
            axisLine={false}
            tickLine={false}
            width={48}
            tickFormatter={fmtMsTick}
          />
          <Tooltip
            contentStyle={{
              backgroundColor: "#1f2937",
              border: "1px solid #374151",
              borderRadius: 6,
              fontSize: 11,
            }}
            labelStyle={{ color: "#e5e7eb" }}
            labelFormatter={(label, payload) =>
              formatDateTime(payload?.[0]?.payload?.ts as string | undefined, String(label))
            }
            formatter={(v) => {
              if (v == null) return "—";
              const n = Number(v);
              return n < 10 ? `${n.toFixed(1)} ms` : `${Math.round(n)} ms`;
            }}
          />
          <Legend wrapperStyle={{ fontSize: 10 }} />
          {keys.map((k, i) => (
            <Line
              key={k}
              type="monotone"
              dataKey={k}
              name={k}
              stroke={SERIES_COLORS[i % SERIES_COLORS.length]}
              strokeWidth={1.5}
              dot={{ r: 3 }}
              connectNulls
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

// ── Sekcja główna ──────────────────────────────────────────────────────────

export function PerfRunsSection() {
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [triggering, setTriggering] = useState(false);

  const { data: runs = [], refetch } = useQuery<PerfRunResult[]>({
    queryKey: ["dev-metrics-perf-runs"],
    queryFn: devMetricsApi.getPerfRuns,
    refetchInterval: (query) => {
      const r = query.state.data as PerfRunResult[] | undefined;
      return r?.some((x) => x.status === "running") ? 5_000 : 30_000;
    },
  });

  const runningRuns = runs.filter((r) => r.status === "running");
  const runningRun = runningRuns[0];

  async function handleTrigger() {
    setTriggering(true);
    try {
      await devMetricsApi.triggerPerfRun();
      setTimeout(() => refetch(), 1500);
    } finally {
      setTriggering(false);
    }
  }

  async function handleAbort(id: string) {
    if (!confirm("Oznaczyć ten run jako ERROR i odwołać task w Celery?")) return;
    await devMetricsApi.abortPerfRun(id);
    refetch();
  }

  async function handleAbortAll() {
    if (runningRuns.length === 0) return;
    if (!confirm(`Oznaczyć ${runningRuns.length} zawieszonych runów jako ERROR?`)) return;
    await Promise.all(runningRuns.map((r) => devMetricsApi.abortPerfRun(r.id)));
    refetch();
  }

  return (
    <section className="bg-gray-900 border border-gray-800 rounded-xl overflow-hidden">
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-800">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm font-semibold text-gray-100">Wydajność QGIS</span>
          <span className="text-xs text-gray-500">codziennie o 21:00 automatycznie</span>
          <InfoBadge text="Każdy run: 2 scenariusze (a_cold_single = 1 VU bez cache, c_concurrent_5 = 5 VU/60s) × 2 targety (internal = nginx→QGIS bezpośrednio, public = przez ALB+Django). Różnica internal/public to koszt warstwy backendu." />
          {runningRun && (
            <span className="text-xs text-blue-400 animate-pulse font-medium">
              ● trwa{runningRuns.length > 1 ? ` (${runningRuns.length})` : ""}...
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {runningRuns.length > 0 && (
            <button
              onClick={handleAbortAll}
              title={`Przerwij ${runningRuns.length === 1 ? "trwający run" : `${runningRuns.length} runów`}`}
              className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium bg-red-900/50 hover:bg-red-800/60 text-red-300 transition-colors"
            >
              <X size={12} />
              <span className="hidden sm:inline">
                Przerwij{runningRuns.length > 1 ? ` (${runningRuns.length})` : ""}
              </span>
            </button>
          )}
          <button
            disabled={triggering || !!runningRun}
            onClick={handleTrigger}
            title="Uruchom teraz"
            className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium bg-gray-800 hover:bg-gray-700 text-gray-200 disabled:opacity-50 transition-colors"
          >
            <RefreshCw size={12} className={triggering ? "animate-spin" : ""} />
            <span className="hidden sm:inline">Uruchom teraz</span>
          </button>
        </div>
      </div>

      <PerfTimelineChart runs={runs} />

      {runs.length === 0 ? (
        <div className="px-4 py-6 text-center text-sm text-gray-500">
          Brak wyników — benchmark jeszcze nie był uruchamiany.
        </div>
      ) : (
        <div className="divide-y divide-gray-800">
          {runs.map((run) => {
            const isRunning = run.status === "running";
            const isExpanded = isRunning || expandedId === run.id;
            const dur =
              run.duration_seconds != null
                ? `${Math.round(run.duration_seconds)}s`
                : "—";

            const p95s = Object.values(run.scenarios_summary || {})
              .map((s) => s?.p95_ms)
              .filter((v): v is number => typeof v === "number");
            const worstP95 = p95s.length ? Math.max(...p95s) : null;

            return (
              <div key={run.id}>
                <button
                  className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-gray-800/50 transition-colors text-left"
                  onClick={() => !isRunning && setExpandedId((prev) => (prev === run.id ? null : run.id))}
                >
                  <span className={`w-2 h-2 rounded-full flex-shrink-0 ${DOT_COLOR[run.status]}`} />
                  <span className={`text-xs font-bold w-16 flex-shrink-0 ${STATUS_COLOR[run.status]}`}>
                    {STATUS_LABEL[run.status]}
                  </span>
                  <span className="text-xs text-gray-400 w-32 flex-shrink-0 tabular-nums">
                    {formatDateTime(run.started_at)}
                  </span>
                  <span className="text-xs text-gray-500 flex-shrink-0">
                    {run.triggered_by === "manual" ? "ręczne" : "auto"}
                  </span>
                  {!isRunning && (
                    <>
                      <span className="hidden md:inline text-xs text-gray-500 ml-2 tabular-nums">
                        max p95:{" "}
                        <span className="text-gray-300 font-semibold">{fmtMs(worstP95)}</span> ms
                      </span>
                      <span className="hidden md:inline text-xs text-gray-600 ml-2">{dur}</span>
                    </>
                  )}
                  <span className="ml-2">
                    <PerfRegressionBadge flags={run.regression_flags || []} />
                  </span>
                  <span className="ml-auto text-gray-500">
                    {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                  </span>
                </button>

                {isExpanded && <PerfRunDetailPanel runId={run.id} />}
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}
